import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Shield, RefreshCcw, Zap } from "lucide-react";
import { api } from "@/lib/api";
import { Button, Card, CardHeader, CardTitle, CardContent, CardDescription, Spinner } from "@/components/ui/primitives";
import { clearAllPlaybackFlags } from "@/lib/playback";

export const Route = createFileRoute("/admin")({ component: AdminPage });

function AdminPage() {
  const qc = useQueryClient();
  const { data: stats, isLoading } = useQuery({ queryKey: ["stats"], queryFn: api.getStats });
  const reset = useMutation({
    mutationFn: api.resetDB,
    onSuccess: () => {
      clearAllPlaybackFlags();
      qc.invalidateQueries();
    },
  });
  const tick = useMutation({
    mutationFn: api.tickAll,
    onSuccess: () => qc.invalidateQueries(),
  });
  return (
    <div className="space-y-6">
      <header>
        <h1 className="flex items-center gap-2 text-3xl font-semibold tracking-tight">
          <Shield className="h-7 w-7 text-brand" strokeWidth={1.7} /> Admin
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">Tools for driving the demo.</p>
      </header>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <Stat label="Total" value={stats?.total} />
        <Stat label="Unprocessed" value={stats?.unprocessed} />
        <Stat label="In progress" value={stats?.in_progress} />
        <Stat label="Completed" value={stats?.completed} />
      </div>
      {isLoading && <div className="text-sm text-muted-foreground"><Spinner /> Loading stats…</div>}

      <Card>
        <CardHeader>
          <CardTitle>Reset database</CardTitle>
          <CardDescription>Truncate all applications and re-seed the demo personas. This cannot be undone.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="destructive" onClick={() => reset.mutate()} disabled={reset.isPending}>
            {reset.isPending ? <Spinner /> : <RefreshCcw className="h-4 w-4" strokeWidth={2.2} />} Reset & re-seed
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Auto-tick pipeline</CardTitle>
          <CardDescription>Advance every application that is currently in-flight by one pipeline stage.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="brand" onClick={() => tick.mutate()} disabled={tick.isPending}>
            {tick.isPending ? <Spinner /> : <Zap className="h-4 w-4" strokeWidth={2.2} />} Advance all in-flight
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value }: { label: string; value?: number }) {
  return (
    <Card className="p-5">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 text-3xl font-semibold tabular-nums">{value ?? "—"}</div>
    </Card>
  );
}
